import type React from "react";
import { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { MiniArrowRight, SubMenuBackArrow } from "assets";
import { theme } from "theme";
import { useHeight } from "utils/hooks";

import { DropdownButton, DropdownItem } from "../helpers";

interface Props {
  children: React.ReactNode;
  closeMenu: () => void;
  open: boolean;
  useBackgroundOverlay?: boolean;
  useToggleIcon?: boolean;
  subOpen?: boolean;
}

export const PatientHeaderDropdown: React.FC<Props> = ({
  children,
  closeMenu,
  open,
  useBackgroundOverlay = true,
  useToggleIcon = false,
  subOpen = false,
}) => {
  const [heightRef, heightMax] = useHeight<HTMLDivElement>();

  return (
    <>
      <Overlay
        $open={open}
        $useBackgroundOverlay={useBackgroundOverlay}
        onClick={closeMenu}
        data-testid="dropdown-overlay"
      />
      <DropdownList
        $open={open && !subOpen}
        $height={heightMax}
        data-testid="dropdown-list"
        style={{
          top: useToggleIcon ? "-2px" : "144px",
          paddingTop: useToggleIcon ? "24px" : "0px",
        }}
      >
        <div ref={heightRef}>{children}</div>
      </DropdownList>
    </>
  );
};

const DropdownList = styled.ul<{ $open: boolean; $height: number }>`
  height: ${props => (props.$open ? `${props.$height}px` : "0")};
  visibility: ${props => (props.$open ? "visible" : "hidden")};
  transition: height 0.5s ease-in-out;
  overflow: hidden;
  display: block;
  margin: 0;
  padding: 0;
  position: absolute;
  right: 0;
  left: 0;
  top: 170px;
  list-style: none;
  background-color: ${props => props.theme.colors.white};
  border-radius: 12px;
  border-top: 1px solid ${props => props.theme.colors.redesign.db20};
  z-index: ${props => props.theme.zIndex.headerDropdown};
`;

const Overlay = styled.div<{ $open: boolean; $useBackgroundOverlay: boolean }>`
  position: fixed;
  top: 144px;
  right: 0;
  bottom: 0;
  left: 0;
  background-color: ${props => (props.$useBackgroundOverlay ? theme.colors.black : "transparent")};
  border-radius: 12px 12px 0 0;
  z-index: ${props => props.theme.zIndex.headerDropdownOverlay};
  opacity: ${props => (props.$open ? 0.65 : 0)};
  pointer-events: ${props => (props.$open ? "auto" : "none")};
  transition: opacity 0.3s ease-in-out;
  :hover {
    cursor: pointer;
  }
`;

interface SubProps {
  isMainOpen: boolean;
  children: React.ReactNode;
  label: React.ReactNode;
  onVisibleChange: (visible: boolean) => void;
}

export const PatientHeaderDropdownSub: React.FC<SubProps> = ({ isMainOpen, children, label, onVisibleChange }) => {
  const [open, setOpen] = useState(false);
  const [heightRef, heightMax] = useHeight<HTMLDivElement>();
  const { t } = useTranslation();

  useEffect(() => {
    if (!isMainOpen) {
      onVisibleChange(false);
      setOpen(false);
    }
  }, [isMainOpen, onVisibleChange]);

  const flipVisibility = () => {
    onVisibleChange(!open);
    setOpen(!open);
  };

  return (
    <DropdownItem>
      <DropdownButton onClick={() => flipVisibility()}>
        <ButtonContent>
          <ButtonContentLabel>{label}</ButtonContentLabel>
          <MiniArrowRight />
        </ButtonContent>
      </DropdownButton>
      <DropdownSubList $open={open && isMainOpen} $height={heightMax}>
        <div ref={heightRef}>
          <DropdownItem>
            <DropdownButton onClick={() => flipVisibility()}>
              <SubMenuBackArrow />
              {t("buttons.back")}
            </DropdownButton>
          </DropdownItem>
          {children}
        </div>
      </DropdownSubList>
    </DropdownItem>
  );
};

const DropdownSubList = styled.ul<{ $open: boolean; $height: number }>`
  height: ${props => (props.$open ? `${props.$height}px` : "0")};
  visibility: ${props => (props.$open ? "visible" : "hidden")};
  transition: height 0.3s ease-in-out;
  overflow: hidden;
  display: block;
  margin: 0;
  padding: 0;
  position: fixed;
  right: 0;
  left: 0;
  top: 144px;
  list-style: none;
  background-color: ${props => props.theme.colors.white};
  border-radius: 12px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.2);
  z-index: ${props => props.theme.zIndex.headerDropdown + 10};
`;

const ButtonContent = styled.div`
  flex: 1;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-direction: row;
`;

const ButtonContentLabel = styled.div`
  display: flex;
  align-items: center;
  > svg {
    margin-right: ${props => props.theme.spacing.S_10};
  }
`;
